import {
    AppBar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputBase,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import PropTypes from 'prop-types';
import React from 'react';
import Button from '../../controls/Button';
import { hasWhiteSpace } from './../../util/regex';
import { constructRulesForSave, deconstructRulesForDialog } from './fieldDialogLogic';
import AlertDetails from './FieldTypeDetails/AlertDetails';
import CheckboxDetails from './FieldTypeDetails/CheckboxDetails';
import DefaultDetails from './FieldTypeDetails/DefaultDetails';
import DropdownDetails from './FieldTypeDetails/DropdownDetails';
import FileUploadDetails from './FieldTypeDetails/FileUploadDetails';
import RepeatableFieldDetails from './FieldTypeDetails/RepeatableFieldDetails';
import TextBlockDetails from './FieldTypeDetails/TextBlockDetails';
import Validation from './Rules/Validation/Validation';
import Visibility from './Rules/Visibility/Visibility';
import FieldMappingSection from './FieldMappingSection';
import { cloneDeep, unset } from 'lodash';

const headerSizing = 'small';

const styles = (theme) => ({
    dialogPaper: {
        minHeight: '90vh',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        margin: 'auto',
    },
    header: {
        fontSize: 24,
    },
    title: {
        marginTop: theme.spacing(5),
        display: 'flex',
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        height: 'auto',
    },
    tabLabel: {
        '&:hover': {
            color: theme.palette.primary.main,
        },
        minWidth: theme.spacing(0),
        height: theme.spacing(0),
        padding: theme.spacing(0),
    },
    section: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        background: '#F8F8F8',
        padding: theme.spacing(3),
    },
    whiteBackground: {
        backgroundColor: 'white',
    },
    dialogPadding: {
        paddingLeft: theme.spacing(7),
        paddingRight: theme.spacing(7),
    },
    actionButtons: {
        padding: theme.spacing(3),
    },
    tabs: {
        gap: '3%',
    },
    root: {
        flexGrow: 1,
    },
    paper: {
        padding: theme.spacing(2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    grey: {
        color: '#484848',
    },
});

function TabContainer(props) {
    return (
        <Typography {...props} component="div">
            {props.children}
        </Typography>
    );
}

TabContainer.propTypes = {
    children: PropTypes.node.isRequired,
};

class FieldDialog extends React.Component {
    state = {
        activeTabIndex: 0,
        field: null,
        tempRules: {
            visibility: {},
            validation: {},
        },
    };

    handleDone = () => {
        const updatedValidationRules = this.state.tempRules.validation.validationRules.map((rule) => {
            const conditions = rule.conditions.map((condition) => {
                if (
                    (condition.operator === 'in' || condition.operator === 'notIn') &&
                    !Array.isArray(condition.value)
                ) {
                    return { ...condition, value: condition.value.split(',').map((element) => element.trim()) };
                } else {
                    return { ...condition, value: condition.value };
                }
            });
            return { ...rule, conditions: conditions };
        });
        const updatedVisibilityConditions = this.state.tempRules.visibility.conditions.map((condition) => {
            if ((condition.operator === 'in' || condition.operator === 'notIn') && !Array.isArray(condition.value)) {
                return { ...condition, value: condition.value.split(',').map((element) => element.trim()) };
            } else {
                return { ...condition, value: condition.value };
            }
        });
        const updatedTempRules = {
            ...this.state.tempRules,
            visibility: { ...this.state.tempRules.visibility, conditions: updatedVisibilityConditions },
            validation: { ...this.state.tempRules.validation, validationRules: updatedValidationRules },
        };
        let updatedField = { ...this.state.field };
        if (updatedField?.multiline === true) {
            updatedField = { ...updatedField, mask: null };
        } else if (updatedField?.multiline === false) {
            const { multiline, minRows, maxRows, ...fieldWithoutMultiline } = updatedField;
            updatedField = fieldWithoutMultiline;
        }
        if (updatedField?.smartUpload === false) {
            const { smartUpload, templateUrl, ...fieldWithoutSmartUpload } = updatedField;
            updatedField = fieldWithoutSmartUpload;
        }

        if (this.state.field.typeId === 'choices') {
            if (this.state.field.choicesFromApi) {
                let queryParamsObj = {};
                if (Array.isArray(updatedField.api.queryParamsArray)) {
                    updatedField.api.queryParamsArray.forEach((queryParam) => {
                        if (queryParam.queryParamType !== '' && queryParam.queryParamValue !== '') {
                            queryParamsObj['filter[where][' + queryParam.queryParamType + ']'] =
                                queryParam.queryParamValue;
                        }
                    });
                    updatedField = { ...updatedField, api: { ...updatedField.api, queryParams: queryParamsObj } };
                }
                delete updatedField.choices;
                updatedField = this.filterFieldObject(updatedField, 'choices');
                updatedField = { ...updatedField, initialValue: '' };
                updatedField = this.filterFieldObject(updatedField, 'api.queryParamsArray');
            } else {
                updatedField = this.filterFieldObject(updatedField, 'api');
            }
            updatedField = this.filterFieldObject(updatedField, 'choicesFromApi');
        }
        let currentRules = constructRulesForSave(updatedField, updatedTempRules);

        this.props.handleChangeField({ ...updatedField, ...currentRules });
        this.setState({
            field: null,
            tempRules: {
                visibility: {},
                validation: {},
            },
            activeTabIndex: 0,
        });
    };

    filterFieldObject = (field, pathToRemove) => {
        const clonedField = cloneDeep(field);
        unset(clonedField, pathToRemove);
        return clonedField;
    };

    handleEnter = () => {
        let tempRules = deconstructRulesForDialog(this.props.field);
        this.setState({
            tempRules: tempRules,
            field: this.props.field,
        });
    };

    handleClose = () => {
        this.setState({
            activeTabIndex: 0,
            field: null,
            tempRules: {
                visibility: {},
                validation: {},
            },
        });
        this.props.handleCloseFieldDialog();
    };

    handleUpdateRules = (ruleComponent) => {
        this.setState((prevState) => ({
            tempRules: { ...prevState.tempRules, ...ruleComponent },
        }));
    };

    handleChange = (event) => {
        const tag = event.target.id || event.target.name;
        const value = event.target.value;
        this.setState((prevState) => ({
            field: {
                ...prevState.field,
                [tag]: value,
            },
        }));
    };

    handleTabChange = (event, value) => {
        this.setState({ activeTabIndex: value });
    };

    isDuplicateFieldName() {
        const names = this.props.factFields.map((field) => field.field);
        const fieldName = this.state.field ? this.state.field.field : null;
        return names.some((name) => name === fieldName);
    }

    hasWhiteSpaceInFieldName() {
        const fieldName = this.state.field ? this.state.field.field : null;
        return hasWhiteSpace(fieldName);
    }

    isEmptyFieldName() {
        const fieldName = this.state.field ? this.state.field.field : null;
        return fieldName === '';
    }

    isEmptyLabelName() {
        const fieldType = this.state.field ? this.state.field.typeId : null;
        if (fieldType === 'textInput' || fieldType === 'shortText') {
            const fieldLabel = this.state.field ? this.state.field.label : null;
            return fieldLabel === '';
        }
        return false;
    }

    isEmptyUrlForChoicesFromApi() {
        if (this.state.field?.typeId === 'choices') {
            if (this.state.field.api?.url === '' || this.state.field.api?.url?.template === '') {
                return true;
            } else if (
                typeof this.state.field.api?.url === 'string' &&
                this.state.field.api.url.split('/')[0] === 'codedValues'
            ) {
                return this.state.field.api.url.split('/')[1] === '';
            }
        }
        return false;
    }

    isEmptyUDOField() {
        if (this.state.field?.fieldMapping?.targetPath?.includes('customData')) {
            const udoMappingComponents = this.state.field.fieldMapping.targetPath.replaceAll("']", '').split("['");
            const udoGroup = udoMappingComponents[1];
            if (!udoGroup) {
                return true;
            }
            if (this.state.field.typeId !== 'multiple') {
                const udoField = udoMappingComponents[2];
                if (!udoField) {
                    return true;
                }
            }
        }
        return false;
    }

    isSmartUploadWithoutTemplate() {
        return this.state.field?.smartUpload && !this.state.field?.template?.id;
    }

    isSmartUploadWithoutThreshold() {
        return this.state.field?.smartUpload && !this.state.field?.template?.scoreThreshold;
    }

    isSmartUploadWithInvalidThreshold() {
        return (
            this.state.field?.smartUpload &&
            (this.state.field?.template?.scoreThreshold > 100 ||
                this.state.field?.template?.scoreThreshold < 0 ||
                isNaN(this.state.field?.template?.scoreThreshold))
        );
    }

    render() {
        let fieldDetails = null;
        let { activeTabIndex } = this.state;
        let fieldError =
            this.isDuplicateFieldName() ||
            this.isEmptyFieldName() ||
            this.hasWhiteSpaceInFieldName() ||
            this.isEmptyLabelName() ||
            this.isEmptyUrlForChoicesFromApi() ||
            this.isEmptyUDOField() ||
            this.isSmartUploadWithoutTemplate() ||
            this.isSmartUploadWithoutThreshold() ||
            this.isSmartUploadWithInvalidThreshold();
        let toolTipForError = '';
        if (this.isDuplicateFieldName() === true) {
            toolTipForError = 'Field name already exist';
        } else if (this.isEmptyFieldName() === true) {
            toolTipForError = 'Empty field name is not allowed';
        } else if (this.hasWhiteSpaceInFieldName() === true) {
            toolTipForError = 'White space is not allowed in field name';
        } else if (this.isEmptyLabelName() === true) {
            toolTipForError = 'Empty field label  not allowed';
        } else if (this.isEmptyUrlForChoicesFromApi() === true) {
            toolTipForError = 'URL fields are required';
        } else if (this.isEmptyUDOField() === true) {
            toolTipForError = 'UDO fields are required';
        } else if (this.isSmartUploadWithoutTemplate() === true) {
            toolTipForError = 'Template must be uploaded for Smart Upload functionality';
        } else if (this.isSmartUploadWithoutThreshold() === true) {
            toolTipForError = 'Percentage must be set for Smart Upload functionality';
        } else if (this.isSmartUploadWithInvalidThreshold() === true) {
            toolTipForError = 'Percentage for Smart Upload functionality must be a number between 0 and 100';
        }

        switch (this.props.field.typeId) {
            case 'choices':
                fieldDetails = (
                    <DropdownDetails
                        handleChange={this.handleChange}
                        field={this.state.field}
                        factFields={this.props.factFields}
                    />
                );
                break;
            case 'boolean':
                fieldDetails = <CheckboxDetails handleChange={this.handleChange} field={this.state.field} />;
                break;
            case 'textBlock':
                fieldDetails = <TextBlockDetails handleChange={this.handleChange} field={this.state.field} />;
                break;
            case 'alert':
                fieldDetails = <AlertDetails handleChange={this.handleChange} field={this.state.field} />;
                break;
            case 'fileUpload':
                fieldDetails = (
                    <FileUploadDetails
                        handleChange={this.handleChange}
                        field={this.state.field}
                        form={this.props.form}
                    />
                );
                break;
            default:
                fieldDetails = <DefaultDetails handleChange={this.handleChange} field={this.state.field} />;
                break;
        }

        return (
            <React.Fragment>
                <Dialog
                    disableBackdropClick
                    fullWidth
                    maxWidth={'md'}
                    open={this.props.open}
                    onEnter={this.handleEnter}
                    onClose={this.handleClose}
                    classes={{ paperScrollPaper: this.props.classes.dialogPaper }}
                    PaperProps={{ 'aria-label': this.props.field.field }}
                    role="heading"
                    aria-level={1}
                >
                    <DialogTitle
                        disableTypography={true}
                        className={this.props.classes.title}
                        classes={{ root: this.props.classes.dialogPadding }}
                    >
                        {fieldError && (
                            <Tooltip title={toolTipForError}>
                                <ErrorIcon className={this.props.classes.errorIcon} color="error" />
                            </Tooltip>
                        )}
                        <InputBase
                            className={this.props.classes.header}
                            id={'field'}
                            value={this.state.field ? this.state.field.field : ''}
                            inputProps={{ 'aria-label': 'Field Name' }}
                            onChange={this.handleChange}
                        />
                    </DialogTitle>

                    <DialogContent classes={{ root: this.props.classes.dialogPadding }}>
                        <AppBar position="static" elevation={0} className={this.props.classes.appBar}>
                            <Tabs
                                value={activeTabIndex}
                                variant="scrollable"
                                onChange={this.handleChange}
                                className={this.props.classes.whiteBackground}
                                indicatorColor="primary"
                                textColor="primary"
                                classes={{ flexContainer: this.props.classes.tabs }}
                            >
                                <Tab
                                    label={
                                        <Typography variant="button" className={this.props.classes.tabLabel}>
                                            Field
                                        </Typography>
                                    }
                                    className={this.props.classes.tabLabel}
                                    onClick={() => this.setState({ activeTabIndex: 0 })}
                                />
                                {this.props.field.typeId !== 'system' && (
                                    <Tab
                                        label={
                                            <Typography variant="button" className={this.props.classes.tabLabel}>
                                                Field Visibility
                                            </Typography>
                                        }
                                        className={this.props.classes.tabLabel}
                                        onClick={() => this.setState({ activeTabIndex: 1 })}
                                    />
                                )}
                                {this.props.field.typeId !== 'system' && (
                                    <Tab
                                        label={
                                            <Typography variant="button" className={this.props.classes.tabLabel}>
                                                Validation
                                            </Typography>
                                        }
                                        className={this.props.classes.tabLabel}
                                        onClick={() => this.setState({ activeTabIndex: 2 })}
                                    />
                                )}
                            </Tabs>
                        </AppBar>
                        <form noValidate className={this.props.classes.form}>
                            <FormControl role="main">
                                {activeTabIndex === 0 && (
                                    <TabContainer className={this.props.classes.section}>
                                        <Grid container direction="column" spacing={4}>
                                            <Grid item>
                                                {this.state.field &&
                                                    this.props.field.typeId !== 'system' &&
                                                    fieldDetails}
                                            </Grid>
                                        </Grid>
                                        <FieldMappingSection
                                            updateField={this.handleChange}
                                            field={this.state.field || {}}
                                            lastSetMapping={this.props.field.fieldMapping}
                                        />
                                        {this.props.field.typeId === 'multiple' && (
                                            <Grid item style={{ padding: '20px 0px' }}>
                                                <RepeatableFieldDetails
                                                    handleChange={this.handleChange}
                                                    field={this.props.field}
                                                />
                                            </Grid>
                                        )}
                                    </TabContainer>
                                )}
                                {activeTabIndex === 1 && (
                                    <TabContainer className={this.props.classes.section}>
                                        {this.state.field ? (
                                            <Visibility
                                                entries={this.props.factFields}
                                                ruleData={this.state.tempRules.visibility}
                                                handleChange={this.handleUpdateRules}
                                            />
                                        ) : null}
                                    </TabContainer>
                                )}
                                {activeTabIndex === 2 && (
                                    <TabContainer>
                                        {this.state.field && (
                                            <Validation
                                                entries={this.props.factFields}
                                                field={this.state.field}
                                                ruleData={this.state.tempRules.validation}
                                                handleChange={this.handleUpdateRules}
                                            />
                                        )}
                                    </TabContainer>
                                )}
                            </FormControl>
                        </form>
                    </DialogContent>
                    <DialogActions classes={{ root: this.props.classes.actionButtons }}>
                        <Button size={headerSizing} onClick={this.handleClose}>
                            Cancel
                        </Button>
                        <Button
                            size={headerSizing}
                            variant="contained"
                            color="primary"
                            disabled={fieldError === true}
                            onClick={this.handleDone}
                        >
                            Done
                        </Button>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

export default withStyles(styles)(FieldDialog);
